import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const CACHE_PREFIX = 'rtb_trl_';
const CACHE_DAYS = 30;

function hashStr(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(36);
}

interface CacheEntry {
  o: string;
  t: string;
  ts: number;
}

function getCache(lang: string, text: string): string | null {
  try {
    const key = `${CACHE_PREFIX}${lang}_${hashStr(text)}`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const e: CacheEntry = JSON.parse(raw);
    if (e.o !== text) return null;
    if (Date.now() - e.ts > CACHE_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(key);
      return null;
    }
    return e.t;
  } catch {
    return null;
  }
}

function setCache(lang: string, text: string, translated: string) {
  try {
    const key = `${CACHE_PREFIX}${lang}_${hashStr(text)}`;
    localStorage.setItem(key, JSON.stringify({ o: text, t: translated, ts: Date.now() }));
  } catch {
    /* storage full */
  }
}

async function fetchTr(text: string, target: string): Promise<string> {
  const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${target}&dt=t&q=${encodeURIComponent(text)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('tr fail');
  const data = await res.json();
  const parts = (data?.[0] || []) as unknown[][];
  return parts.map((p) => (p?.[0] as string) || '').join('') || text;
}

export function useAutoTranslate() {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'zh';

  const translate = useCallback(
    async (text: string): Promise<string> => {
      if (!text || lang === 'zh') return text;
      const cached = getCache(lang, text);
      if (cached) return cached;
      try {
        const result = await fetchTr(text, lang);
        setCache(lang, text, result);
        return result;
      } catch {
        return text;
      }
    },
    [lang],
  );

  return { translate, lang };
}

export function useTranslatedText(text: string): string {
  const [out, setOut] = useState(text);
  const { translate, lang } = useAutoTranslate();
  const ref = useRef(text);

  useEffect(() => {
    ref.current = text;
    if (lang === 'zh' || !text) {
      setOut(text);
      return;
    }
    let ok = true;
    translate(text).then((r) => {
      if (ok && ref.current === text) setOut(r);
    });
    return () => {
      ok = false;
    };
  }, [text, lang, translate]);

  return out;
}

/** Inline component: renders translated text; falls back to original while loading */
export function T({ text }: { text: string }) {
  const out = useTranslatedText(text);
  return <>{out || text}</>;
}